<template>
  <OnboardingStep
    v-bind="$attrs"
    :commit="validateAndCommit"
    :error="errorMessage"
    :form-id="formId"
    :navigating.sync="isNavigating"
    v-on="$listeners"
  >
    <template #notice-top><slot name="notice-top" /></template>
    <template #top><slot name="top" /></template>

    <template #heading><slot name="heading" /></template>
    <template #default="scope">
      <Card :heading="cardHeading">
        <BaseForm v-bind="childFormProps" :id="formId" @submit="scope.next()">
          <slot v-bind="scope" />
        </BaseForm>
      </Card>
    </template>
    <template #sidebar="scope"><slot name="sidebar" v-bind="scope" /></template>
    <template #before-actions><slot name="before-actions" /></template>
    <template #bottom><slot name="bottom" /></template>
  </OnboardingStep>
</template>

<script>
import OnboardingStep from '@/components/onboarding/OnboardingStep';
import { toNonEmptyStringArray } from '@/helpers/convert';
import form from '@/mixins/form';

export default {
  name: 'OnboardingFormStep',
  inheritAttrs: false,
  mixins: [form],
  components: { OnboardingStep },
  props: {
    /**
     * The heading text that appears in the main card.
     *
     * @type {import('vue/types/options').PropOptions<unknown>}
     */
    cardHeading: {
      type: String,
      default: null,
    },
    /**
     * An optional function that is invoked whenever an attempt is made to complete the
     * current step and advance to the next step.
     * - Its `this` value is a reference to the parent component.
     * - If this function returns `false` (or a promise that resolves with `false`), then the
     *   operation is canceled and the user will remain on the current step.
     *
     * If this function is not provided, then attempts to complete the current step are always
     * successful.
     *
     * @type {import('vue/types/options').PropOptions<((this: Vue) => any) | null | undefined>}
     */
    commit: {
      type: Function,
      default: null,
    },
    /**
     * The error(s) currently preventing the user from continuing to the next step.
     *
     * @type {import('vue/types/options').PropOptions<unknown>}
     */
    error: {
      default: null,
    },
  },
  data() {
    return {
      /** @type {boolean} */
      isNavigating: false,
    };
  },
  computed: {
    /**
     * @returns {Record<PropertyKey, unknown>}
     */
    childFormProps() {
      return {
        disabled: !!this.isNavigating,
        loading: this.isLoading,
        verb: null,
      };
    },
    errorMessage: {
      /**
       * @returns {string[]}
       */
      get() {
        return toNonEmptyStringArray([this.error, this.errorMessageData], (e) => e.trim());
      },
      /**
       * @param {string[]} value
       * @returns {void}
       */
      set(value) {
        this.errorMessageData = value;
      },
    },
    /**
     * @returns {string}
     */
    formId() {
      return 'onboarding-step-form';
    },
    /**
     * @returns {boolean}
     */
    isLoading() {
      return !!this.loading || this.pendingActions > 0 || !!this.isNavigating;
    },
  },
  methods: {
    /**
     * @returns {unknown}
     */
    validateAndCommit() {
      if (!this.reportValidity()) {
        return false;
      }
      const { commit, $parent: parent } = this;
      if (!parent || typeof commit !== 'function') {
        return;
      }
      try {
        return commit.call(parent);
      } catch (error) {
        return this.handleError(error);
      }
    },
  },
};
</script>
